import { useMutation, useQuery } from '@tanstack/react-query';
import {
  type Documentation_CreatePageInput,
  type Documentation_MovePageInput,
  type Documentation_PageMetadataKey,
  type Documentation_RelatedPagesFilter,
  type Documentation_SinglePageFilter,
  type Documentation_UpdatePageInput,
  gqlClient,
  graphql,
  type QueryAdditionalOptions,
  queryClient,
  resolveBackendError,
  type UseQueryReturnType,
} from '@tyro/api';
import { toast } from '@tyro/core/toaster';
import { useTranslation } from '@tyro/i18n';
import { documentationKeys } from './keys';

const documentationPages = graphql(/* GraphQL */ `
  query documentation_user_pages($filter: Documentation_PageFilter!) {
    documentation_user_pages(filter: $filter) {
      id
      spaceId
      parentPageId
      slug
      title
      sortOrder
      userTypes
      publishedVersionId
      entityType
      videoData {
        durationSec
      }
    }
  }
`);

const documentationAdminPages = graphql(/* GraphQL */ `
  query documentation_admin_pages($filter: Documentation_PageFilter!) {
    documentation_admin_pages(filter: $filter) {
      page {
        id
        spaceId
        parentPageId
        slug
        title
        sortOrder
        userTypes
        publishedVersionId
        entityType
        videoData {
          durationSec
        }
      }
      activeCrStatus
      metadata {
        id
        pageId
        blockId
        key
        value
      }
    }
  }
`);

const documentationPage = graphql(/* GraphQL */ `
  query documentation_user_page($filter: Documentation_SinglePageFilter!) {
    documentation_user_page(filter: $filter) {
      id
      spaceId
      parentPageId
      slug
      title
      sortOrder
      userTypes
      publishedVersionId
      contentMd
      entityType
      videoData {
        sourceUrl
        provider
        embedUrl
        thumbnailUrl
        durationSec
        aspectRatio
      }
      brokenLinkCount
      pendingLinkCount
    }
  }
`);

const documentationUserPageMetadata = graphql(/* GraphQL */ `
  query documentation_user_pageMetadata($pageIds: [Int!]!) {
    documentation_user_pageMetadata(pageIds: $pageIds) {
      id
      pageId
      blockId
      key
      value
    }
  }
`);

const documentationPageDetail = graphql(/* GraphQL */ `
  query documentation_admin_pageDetail($filter: Documentation_SinglePageFilter!) {
    documentation_admin_pageDetail(filter: $filter) {
      page {
        id
        spaceId
        parentPageId
        slug
        title
        sortOrder
        userTypes
        publishedVersionId
        contentMd
        entityType
        videoData {
          sourceUrl
          provider
          embedUrl
          thumbnailUrl
          durationSec
          aspectRatio
        }
        brokenLinkCount
        pendingLinkCount
      }
      metadata {
        id
        pageId
        blockId
        key
        value
      }
      activeChangeRequest {
        changeRequest {
          id
          title
          status
          createdOn
          releaseTag
        }
        contentMd
        videoData {
          sourceUrl
          provider
          embedUrl
          thumbnailUrl
          durationSec
          aspectRatio
        }
      }
    }
  }
`);

const refreshVideoDataMutation = graphql(/* GraphQL */ `
  mutation documentation_admin_refreshVideoData($input: Documentation_RefreshVideoDataInput!) {
    documentation_admin_refreshVideoData(input: $input) {
      sourceUrl
      provider
      embedUrl
      thumbnailUrl
      durationSec
      aspectRatio
    }
  }
`);

const replacePageVideoUrlMutation = graphql(/* GraphQL */ `
  mutation documentation_admin_replacePageVideoUrl($input: Documentation_ReplacePageVideoUrlInput!) {
    documentation_admin_replacePageVideoUrl(input: $input) {
      sourceUrl
      provider
      embedUrl
      thumbnailUrl
      durationSec
      aspectRatio
    }
  }
`);

const createPage = graphql(/* GraphQL */ `
  mutation documentation_admin_createPage($input: Documentation_CreatePageInput!) {
    documentation_admin_createPage(input: $input) {
      id
      spaceId
      slug
      title
    }
  }
`);

const updatePage = graphql(/* GraphQL */ `
  mutation documentation_admin_updatePage($input: Documentation_UpdatePageInput!) {
    documentation_admin_updatePage(input: $input) {
      id
      title
      userTypes
    }
  }
`);

const deletePages = graphql(/* GraphQL */ `
  mutation documentation_admin_deletePages($pageIds: [Int!]!) {
    documentation_admin_deletePages(pageIds: $pageIds) {
      success
    }
  }
`);

const movePage = graphql(/* GraphQL */ `
  mutation documentation_admin_movePage($input: Documentation_MovePageInput!) {
    documentation_admin_movePage(input: $input) {
      success
    }
  }
`);

const setPageMetadata = graphql(/* GraphQL */ `
  mutation documentation_admin_setPageMetadata($input: Documentation_SetPageMetadataInput!) {
    documentation_admin_setPageMetadata(input: $input) {
      success
    }
  }
`);

const pagesQuery = (spaceId: number) => ({
  queryKey: documentationKeys.pages(spaceId),
  queryFn: async () => {
    const result = await gqlClient.request(documentationPages, {
      filter: { spaceId },
    });
    return result.documentation_user_pages;
  },
});

export function getDocumentationPages(spaceId: number) {
  return queryClient.fetchQuery(pagesQuery(spaceId));
}

export function useDocumentationPages(spaceId: number) {
  return useQuery({
    ...pagesQuery(spaceId),
    enabled: !!spaceId,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

const adminPagesQuery = (spaceId: number) => ({
  queryKey: documentationKeys.adminPages(spaceId),
  queryFn: async () => {
    const result = await gqlClient.request(documentationAdminPages, {
      filter: { spaceId },
    });
    return result.documentation_admin_pages;
  },
});

export function getDocumentationAdminPages(spaceId: number) {
  return queryClient.fetchQuery(adminPagesQuery(spaceId));
}

export function useDocumentationAdminPages(spaceId: number) {
  return useQuery({
    ...adminPagesQuery(spaceId),
    enabled: !!spaceId,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

const documentationAdminAllPages = graphql(/* GraphQL */ `
  query documentation_admin_allPages {
    documentation_admin_allPages {
      id
      spaceId
      spaceSlug
      spaceName
      slug
      title
      entityType
      publishedVersionId
    }
  }
`);

const allAdminPagesQuery = () => ({
  queryKey: documentationKeys.allAdminPages(),
  queryFn: async () => {
    const result = await gqlClient.request(documentationAdminAllPages);
    return result.documentation_admin_allPages.map((row) => ({
      ...row,
      hasPublishedVersion: row.publishedVersionId != null,
    }));
  },
});

export function useAllAdminPages() {
  return useQuery({
    ...allAdminPagesQuery(),
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });
}

export type AdminPageRow = NonNullable<
  UseQueryReturnType<typeof useAllAdminPages>
>[number];

const documentationRelatedPages = graphql(/* GraphQL */ `
  query documentation_user_relatedPages($filter: Documentation_RelatedPagesFilter) {
    documentation_user_relatedPages(filter: $filter) {
      pageId
      spaceId
      title
      spaceSlug
      spaceName
      pageSlug
      entityType
      ancestorTitles
    }
  }
`);

export function useDocumentationRelatedPages(
  filter: Documentation_RelatedPagesFilter,
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  return useQuery({
    queryKey: documentationKeys.relatedPages(filter),
    queryFn: async () => {
      const result = await gqlClient.request(documentationRelatedPages, {
        filter,
      });
      return result.documentation_user_relatedPages;
    },
    staleTime: 60_000,
    ...additionalOptions,
  });
}

const pageQuery = (filter: Documentation_SinglePageFilter) => ({
  queryKey: documentationKeys.page(filter),
  queryFn: async () => {
    const result = await gqlClient.request(documentationPage, { filter });
    return result.documentation_user_page;
  },
});

export function getDocumentationPage(filter: Documentation_SinglePageFilter) {
  return queryClient.fetchQuery(pageQuery(filter));
}

export function useDocumentationPage(filter: Documentation_SinglePageFilter) {
  return useQuery({
    ...pageQuery(filter),
    enabled: !!(filter.pageId || (filter.spaceSlug && filter.pageSlug)),
  });
}

const pageMetadataQuery = (pageId: number) => ({
  queryKey: documentationKeys.pageMetadata(pageId),
  queryFn: async () => {
    const result = await gqlClient.request(documentationUserPageMetadata, {
      pageIds: [pageId],
    });
    return result.documentation_user_pageMetadata;
  },
});

// User-facing page metadata. The reader needs metadata (block-access tags,
// quality) but must not call the admin `documentation_admin_pageDetail` query.
export function useDocumentationPageMetadata(pageId: number | undefined) {
  return useQuery({
    ...pageMetadataQuery(pageId ?? 0),
    enabled: pageId != null,
    staleTime: 60 * 1000,
  });
}

const pageDetailQuery = (filter: Documentation_SinglePageFilter) => ({
  queryKey: documentationKeys.pageDetail(filter),
  queryFn: async () => {
    const result = await gqlClient.request(documentationPageDetail, { filter });
    return result.documentation_admin_pageDetail;
  },
});

export function getDocumentationPageDetail(
  filter: Documentation_SinglePageFilter,
) {
  return queryClient.fetchQuery(pageDetailQuery(filter));
}

export function useDocumentationPageDetail(
  filter: Documentation_SinglePageFilter,
) {
  return useQuery({
    ...pageDetailQuery(filter),
    enabled: !!(filter.pageId || (filter.spaceSlug && filter.pageSlug)),
  });
}

export function useCreatePage() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (input: Documentation_CreatePageInput) =>
      gqlClient.request(createPage, { input }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: documentationKeys.pages(variables.spaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: documentationKeys.adminPages(variables.spaceId),
        }),
        queryClient.invalidateQueries({
          queryKey: documentationKeys.allAdminPages(),
        }),
      ]);
      toast.success(t('common:snackbarMessages.createSuccess'));
    },
    onError: async (error: unknown) => {
      const description = await resolveBackendError(error);
      toast.error(description || t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useUpdatePage() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (input: Documentation_UpdatePageInput) =>
      gqlClient.request(updatePage, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.all,
      });
      toast.success(t('common:snackbarMessages.updateSuccess'));
    },
    onError: async (error: unknown) => {
      const description = await resolveBackendError(error);
      toast.error(description || t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useDeletePages() {
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: (pageIds: number[]) =>
      gqlClient.request(deletePages, { pageIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.all,
      });
      toast.success(t('common:snackbarMessages.deleteSuccess'));
    },
    onError: () => {
      toast.error(t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useMovePage() {
  return useMutation({
    mutationFn: (input: Documentation_MovePageInput) =>
      gqlClient.request(movePage, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.all,
      });
    },
  });
}

export function useRefreshVideoData() {
  const { t } = useTranslation(['common']);
  return useMutation({
    mutationFn: (pageId: number) =>
      gqlClient.request(refreshVideoDataMutation, { input: { pageId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.all,
      });
      toast.success(t('common:snackbarMessages.updateSuccess'));
    },
    onError: async (error: unknown) => {
      const description = await resolveBackendError(error);
      toast.error(description || t('common:snackbarMessages.errorFailed'));
    },
  });
}

export function useReplacePageVideoUrl() {
  return useMutation({
    mutationFn: (input: { pageId: number; sourceUrl: string }) =>
      gqlClient.request(replacePageVideoUrlMutation, { input }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.all,
      });
    },
    onError: async (error: unknown) => {
      const description = await resolveBackendError(error);
      if (description) toast.error(description);
    },
  });
}

export type PageMetadataItemInput = {
  pageId: number;
  key: Documentation_PageMetadataKey;
  values: string[];
  blockId?: string;
};

export function useSetPageMetadata() {
  return useMutation({
    mutationFn: (items: PageMetadataItemInput[]) =>
      gqlClient.request(setPageMetadata, { input: { items } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: documentationKeys.all,
      });
    },
  });
}

export type ReturnTypeFromUseDocumentationPages = UseQueryReturnType<
  typeof useDocumentationPages
>[number];

export type ReturnTypeFromUseDocumentationAdminPages = UseQueryReturnType<
  typeof useDocumentationAdminPages
>[number];

export type ReturnTypeFromUseDocumentationRelatedPages = UseQueryReturnType<
  typeof useDocumentationRelatedPages
>[number];

export type ReturnTypeFromUseDocumentationPageDetail = UseQueryReturnType<
  typeof useDocumentationPageDetail
>;

export type ReturnTypeFromUseDocumentationPage = UseQueryReturnType<
  typeof useDocumentationPage
>;

export type ReturnTypeFromUseDocumentationPageMetadata = UseQueryReturnType<
  typeof useDocumentationPageMetadata
>[number];
